import { normalizeNationalMobileDigits } from '@/lib/auth/normalizeNationalMobile';
import { getBackendBaseUrl, getLoginPath } from './config';

function getAuthApiBase(): string {
  const base = getBackendBaseUrl();
  return base ? `${base}/api` : '/api';
}

const API_BASE = getAuthApiBase();
/** جدا از توکن مدیریت پروژه — هر اپ session مستقل */
const TOKEN_KEY = 'accounting_auth_token';

export interface LoginPayload {
  username: string;
  password: string;
}

export interface OtpLoginRequestPayload {
  /** ۱۰ رقم بعد از +۹۸ */
  mobile_national: string;
}

export interface User {
  id: number;
  name: string;
  username: string;
  email?: string;
}

export interface LoginResponse {
  message: string;
  user: User;
  token: string;
}

export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
}

/** برای ورود / OTP — بدون Bearer تا توکن کهنه مانع نشود */
function getPublicJsonHeaders(): HeadersInit {
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
}

export function getAuthHeaders(extra?: HeadersInit): HeadersInit {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(extra as Record<string, string>),
  };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function handleResponse<T>(r: Response, options?: { isLogin?: boolean }): Promise<T> {
  const text = await r.text();
  if (!r.ok) {
    if (r.status === 401 && typeof window !== 'undefined' && !options?.isLogin) {
      clearAuthToken();
      window.location.href = getLoginPath();
      return new Promise(() => {});
    }
    let msg = `خطا: ${r.status}`;
    try {
      if (text) {
        const j = JSON.parse(text) as { message?: string; error?: string };
        msg = j.message ?? j.error ?? msg;
      }
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (!text?.trim()) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('پاسخ نامعتبر');
  }
}

export const authApi = {
  async login(payload: LoginPayload): Promise<LoginResponse> {
    const r = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });
    const data = await handleResponse<LoginResponse>(r, { isLogin: true });
    if (data.token) setAuthToken(data.token);
    return data;
  },

  async logout(): Promise<void> {
    const token = getAuthToken();
    if (token) {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
      } finally {
        clearAuthToken();
      }
    } else {
      clearAuthToken();
    }
  },

  async fetchUser(): Promise<User> {
    const r = await fetch(`${API_BASE}/auth/user`, { headers: getAuthHeaders() });
    const data = await handleResponse<{ user: User }>(r);
    return data.user;
  },

  async requestLoginOtp(payload: OtpLoginRequestPayload): Promise<{ success?: boolean; message?: string }> {
    const mobile_national = normalizeNationalMobileDigits(payload.mobile_national);
    if (!mobile_national) {
      throw new Error('شماره موبایل معتبر نیست. مثال: ۰۹۱۲… یا ۹۱۲… یا با پیش‌شماره ۹۸.');
    }
    const r = await fetch(`${API_BASE}/auth/login-otp/request`, {
      method: 'POST',
      headers: getPublicJsonHeaders(),
      body: JSON.stringify({ mobile_national }),
    });
    return handleResponse<{ success?: boolean; message?: string }>(r, { isLogin: true });
  },

  async verifyLoginOtp(payload: { mobile_national: string; code: string }): Promise<LoginResponse> {
    const mobile_national = normalizeNationalMobileDigits(payload.mobile_national);
    if (!mobile_national) {
      throw new Error('شماره موبایل معتبر نیست.');
    }
    const r = await fetch(`${API_BASE}/auth/login-otp/verify`, {
      method: 'POST',
      headers: getPublicJsonHeaders(),
      body: JSON.stringify({
        mobile_national,
        code: payload.code.replace(/\D/g, '').slice(0, 5),
      }),
    });
    const data = await handleResponse<LoginResponse>(r, { isLogin: true });
    if (data.token) setAuthToken(data.token);
    return data;
  },
};
